import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Minus, Plus, MessageCircle, Truck, Shield, Ruler } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { CartContext, API } from '../App';
import { toast } from 'sonner';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart } = useContext(CartContext);


  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/products/${id}`);
      if (!response.ok) throw new Error('Product not found');
      const data = await response.json();
      setProduct(data);
      
      // Fetch related products
      const relatedResponse = await fetch(`${API}/products/category/${data.category}/related?exclude_id=${id}`);
      const relatedData = await relatedResponse.json();
      setRelatedProducts(relatedData);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart`);
  };

  const handleWhatsApp = async () => {
    try {
      const response = await fetch(`${API}/whatsapp-link?product_name=${encodeURIComponent(product.name)}`);
      const data = await response.json();
      window.open(data.link, '_blank');
    } catch (error) {
      window.open(`https://wa.me/919821767267?text=Hi, I am interested in the ${product.name}. Can you provide more details?`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-gray-200 animate-pulse"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 w-1/4 animate-pulse"></div>
                <div className="h-8 bg-gray-200 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-gray-200 w-1/3 animate-pulse"></div>
                <div className="h-20 bg-gray-200 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 pb-12 text-center">
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Product not found</h1>
          <Link to="/products" className="btn-primary">Back to Products</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center text-sm text-[#666666] hover:text-[#1A1A1A]" data-testid="back-to-products">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Products
          </Link>
        </div>
      </div>

      {/* Product Details */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="bg-[#F9F9F8]">
              <img 
                src={(product.image_urls && product.image_urls.length > 0) ? product.image_urls[selectedImage] : (product.image_url || 'https://via.placeholder.com/800x800?text=in-dekor')} 
                alt={product.name}
                className="w-full h-auto object-contain aspect-square"
                data-testid="product-image"
              />
              {product.image_urls && product.image_urls.length > 1 && (
                <div className="flex gap-2 mt-3 px-2 pb-2">
                  {product.image_urls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={product.name + ' view ' + (index + 1)}
                      onClick={() => setSelectedImage(index)}
                      className={"w-20 h-20 object-cover cursor-pointer border-2 " + (selectedImage === index ? "border-[#C4A882]" : "border-transparent")}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C4A882] mb-2" data-testid="product-category">
                {product.category}
              </p>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] tracking-tight mb-4" data-testid="product-name">
                {product.name}
              </h1>
              <p className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-6" data-testid="product-price">
                {product.price_on_request ? 'Price on Request' : `₹${product.price.toLocaleString('en-IN')}`}
              </p>
              
              <p className="font-body text-[#666666] leading-relaxed mb-8" data-testid="product-description">
                {product.description}
              </p>

              {/* Specifications */}
              <div className="border-t border-b border-[#E5E5E5] py-6 mb-8 space-y-3">
                <div className="flex">
                  <span className="font-body text-sm text-[#666666] w-28">Dimensions</span>
                  <span className="font-body text-sm text-[#1A1A1A]" data-testid="product-dimensions">{product.dimensions}</span>
                </div>
                <div className="flex">
                  <span className="font-body text-sm text-[#666666] w-28">Material</span>
                  <span className="font-body text-sm text-[#1A1A1A]" data-testid="product-material">{product.material}</span>
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              {!product.price_on_request && (
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex items-center border border-[#E5E5E5]">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-3 hover:bg-[#F9F9F8] transition-colors"
                      data-testid="decrease-quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-6 py-3 font-body text-center min-w-[60px]" data-testid="quantity-value">{quantity}</span>
                    <button 
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-4 py-3 hover:bg-[#F9F9F8] transition-colors"
                      data-testid="increase-quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <button 
                    onClick={handleAddToCart}
                    className="btn-primary flex-1"
                    data-testid="add-to-cart-btn"
                  >
                    Add to Cart
                  </button>
                </div>
              )}

              {/* WhatsApp Inquiry */}
              <button 
                onClick={handleWhatsApp}
                className="w-full btn-secondary flex items-center justify-center mb-8"
                data-testid="whatsapp-inquiry-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Enquire on WhatsApp
              </button>

              {/* Features */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-[#F9F9F8]">
                  <Truck className="w-5 h-5 mx-auto mb-2 text-[#C4A882]" />
                  <p className="font-body text-xs text-[#666666]">Free Mumbai Delivery</p>
                </div>
                <div className="p-4 bg-[#F9F9F8]">
                  <Shield className="w-5 h-5 mx-auto mb-2 text-[#C4A882]" />
                  <p className="font-body text-xs text-[#666666]">Craftsmanship Guarantee</p>
                </div>
                <div className="p-4 bg-[#F9F9F8]">
                  <Ruler className="w-5 h-5 mx-auto mb-2 text-[#C4A882]" />
                  <p className="font-body text-xs text-[#666666]">Custom Sizing Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-[#F9F9F8]" data-testid="related-products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-semibold text-[#1A1A1A] tracking-tight mb-8">
              Related Products
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedProducts.map((relProduct) => (
                <div 
                  key={relProduct.id} 
                  className="product-card group bg-white"
                  data-testid={`related-product-${relProduct.id}`}
                >
                  <Link to={`/products/${relProduct.id}`}>
                    <div className="aspect-[4/3] overflow-hidden mb-4">
                      <img 
                        src={(relProduct.image_urls && relProduct.image_urls.length > 0) ? relProduct.image_urls[0] : (relProduct.image_url || 'https://via.placeholder.com/400x300?text=in-dekor')} 
                        alt={relProduct.name}
                        className="w-full h-full object-cover transition-transform duration-500"
                      />
                    </div>
                  </Link>
                  <div className="p-4">
                    <p className="font-body text-xs uppercase tracking-[0.15em] text-[#666666] mb-1">
                      {relProduct.category}
                    </p>
                    <Link to={`/products/${relProduct.id}`}>
                      <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-1 group-hover:text-[#C4A882] transition-colors">
                        {relProduct.name}
                      </h3>
                    </Link>
                    <p className="font-heading text-lg font-semibold text-[#1A1A1A]">
                      {relProduct.price_on_request ? 'Price on Request' : `₹${relProduct.price.toLocaleString('en-IN')}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
